import React, { useState } from "react";
import { IOptionGroupProps } from "@atoms/ZSelect/interface";
import {
	DescimalRountingPrecision,
	WholeNumberRountingPrecision,
	RoundingType,
	RoundingDetails
} from "@/Contracts/Settings";
import {
	ZButton,
	ZIcon,
	ZSelect,
	ZTooltip,
	ZTypography
} from "@atoms";
import { Divider, Layout, Switch } from "antd";
import { SellPriceRoundingOptionProps } from "./interface";
import { EColors } from "@atoms/ZColors/interface";
import "./index.less";
import { EIcons } from "@atoms/ZIcon/interface";
import round from "./RoundingUtils";

const { ZText } = ZTypography;

const { Header, Footer, Content } = Layout;

const ConfigSellPriceRoundingOption: React.FC<SellPriceRoundingOptionProps> = ({
	roundingDetails,
	handleRoundingDetailsChange,
	handleClose,
	calculatedSellPrice = 0
}) => {
	const roundingData = roundingDetails ?? new RoundingDetails();
	const [isEnabled, setIsEnabled] = useState<boolean>(
		roundingData.is_enabled
	);
	const [precision, setPrecision] = useState<
		DescimalRountingPrecision | WholeNumberRountingPrecision
	>(roundingData.precision);
	const [method, setMethod] = useState<RoundingType>(roundingData.method);

	const handleSave = () => {
		const newRoundingDetails = new RoundingDetails();
		newRoundingDetails.is_enabled = isEnabled;
		newRoundingDetails.precision = precision;
		newRoundingDetails.method = method;
		handleRoundingDetailsChange(newRoundingDetails);
		handleClose?.();
	};

	const PrecisionOptions: Array<IOptionGroupProps> = [
		{
			label: "Decimal",
			selectOptions: [
				{
					name: `Hundreds (${DescimalRountingPrecision.Hundreds})`,
					value: DescimalRountingPrecision.Hundreds
				},
				{
					name: `Tens (${DescimalRountingPrecision.Tens})`,
					value: DescimalRountingPrecision.Tens
				}
			]
		},
		{
			label: "Whole Number",
			selectOptions: [
				{
					name: `Ones (${WholeNumberRountingPrecision.Ones})`,
					value: WholeNumberRountingPrecision.Ones
				},
				{
					name: `Tens (${WholeNumberRountingPrecision.Tens})`,
					value: WholeNumberRountingPrecision.Tens
				},
				{
					name: `Hundreds (${WholeNumberRountingPrecision.Hundreds})`,
					value: WholeNumberRountingPrecision.Hundreds
				},
				{
					name: `Thousands (${WholeNumberRountingPrecision.Thousands})`,
					value: WholeNumberRountingPrecision.Thousands
				},
				{
					name: `Millions (${WholeNumberRountingPrecision.Millions})`,
					value: WholeNumberRountingPrecision.Millions
				}
			]
		}
	];

	const RoundingModeOptions = [
		{
			label: `Round to the nearest (default)`,
			value: RoundingType.Nearest
		},
		{ label: "Round half up", value: RoundingType.HalfUp },
		{ label: "Round half down", value: RoundingType.HalfDown },
		{ label: "Round up (ceiling)", value: RoundingType.Ceiling },
		{ label: "Round down (floor)", value: RoundingType.Floor },
		{ label: "Round half to even", value: RoundingType.HalfToEven },
		{ label: "Round half to odd", value: RoundingType.HalfToOdd },
		{
			label: "Round half away from zero",
			value: RoundingType.HalfAwayFromZero
		},
		{
			label: "Round half towards zero",
			value: RoundingType.HalfTowardsZero
		}
	];

	const roundedSellPrice = round(calculatedSellPrice, precision, method);

	return (
		<Layout
			data-testid="rounding-options"
			className="config-sell-price-rounding"
		>
			<Header className="flex items-center h-4 mb-4">
				<Switch
					disabled={false}
					checked={isEnabled}
					size="small"
					onChange={checked => setIsEnabled(checked)}
					data-testid="rounding-enable-switch"
				/>
				<ZText className="ml-2" color={EColors.GRAY} colorVariant={8}>
					Enable Rounding
				</ZText>
				<ZTooltip title="Round off the sell price automatically to the desired format after it is updated.">
					<ZIcon
						size="sm"
						className="ml-2"
						name={EIcons.WARNINGCIRCLE}
						color={EColors.GRAY}
						colorVariant={6}
					/>
				</ZTooltip>
			</Header>
			<Divider />
			<Content className="flex flex-col">
				<ZText
					className="mt-4 mb-1"
					color={EColors.GRAY}
					colorVariant={isEnabled ? 9 : 7}
				>
					Precision
				</ZText>
				<ZSelect
					isOptionsGroup={true}
					disabled={!isEnabled}
					optionsGroup={PrecisionOptions}
					defaultValue={precision}
					onChange={value => setPrecision(value)}
					dropdownClassName="sellprice-precision-options"
				/>
				<ZText color={EColors.GRAY} colorVariant={isEnabled ? 9 : 7}>
					{calculatedSellPrice}{" "}
					{
						<ZIcon
							size="sm"
							className="mb-0.5 mx-2"
							name={EIcons.ARROWRIGHT}
							color={EColors.GRAY}
							colorVariant={isEnabled ? 6 : 5}
						/>
					}{" "}
					{roundedSellPrice}
				</ZText>
				<ZText
					className="mt-4 mb-1"
					color={EColors.GRAY}
					colorVariant={isEnabled ? 9 : 7}
				>
					Rounding Mode
					<ZText
						className="mt-4 mb-1 text-0"
						color={EColors.GRAY}
						colorVariant={6}
					>
						{" "}
						(Optional)
					</ZText>
				</ZText>
				<ZSelect
					options={RoundingModeOptions}
					disabled={!isEnabled}
					defaultValue={method}
					onChange={value => setMethod(value)}
				/>
			</Content>
			<Footer className="flex items-center justify-end mt-6">
				<ZButton
					size="small"
					className="mr-2"
					onClick={() => handleClose?.()}
				>
					Cancel
				</ZButton>
				<ZButton
					type="primary"
					size="small"
					data-testid="rounding-save-btn"
					onClick={handleSave}
				>
					Save
				</ZButton>
			</Footer>
		</Layout>
	);
};

export default ConfigSellPriceRoundingOption;
